import { CostsRequester, GraphQLDriversRequester, isWebDataSuccess, NewMaintenanceRequester, type WebData } from '@cobliteam/api-lib';
import type { MaintenanceCosts as NewMaintenanceCosts } from '@cobliteam/api-lib/src/dashboard/maintenance/Types';
import type { DriverNames, FuelCostByDay, FuelCosts, MaintenanceCosts, UUID } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { downloadCostsEnd, loadCostsFailed, loadCostsSucceeded, loadDriverNamesSucceeded } from './actionCreators';
import type { DownloadCostsRequest, LoadCostsRequest } from './actions';
import { ActionTypes } from './actionTypes';
import { selectDriverNamesById } from './selectors';

const env = Environment.getInstance().getEnvironment();
export const costsRequester = new CostsRequester({ host: env.herbieApiHost });
export const newMaintenanceRequester = new NewMaintenanceRequester({ host: env.apiHost });
export const driversRequester = new GraphQLDriversRequester();

const getDriversIdFromFuelCosts = (fuelCosts: FuelCosts) =>
  Object.values(fuelCosts).flatMap((vehicleFuelCosts: FuelCostByDay[]) => vehicleFuelCosts.flatMap((fuelCostsByDay: FuelCostByDay) => fuelCostsByDay.driversId));

const downloadReportFile = (blob: Blob, name: string, startInMilli: number, endInMilli: number): void => {
  const startTimeStr = moment(startInMilli).format('YYMMDD');
  const endTimeStr = moment(endInMilli).format('YYMMDD');
  const periodStr = startTimeStr === endTimeStr ? startTimeStr : `${startTimeStr}_${endTimeStr}`;

  const filename = `Cobli_${periodStr}_${name}.xlsx`;
  saveAs(blob, filename);
};

export const getMissingDriversId = (driverNamesWebData: WebData<DriverNames>, fuelCosts: FuelCosts) => {
  const neededDrivers = getDriversIdFromFuelCosts(fuelCosts);
  const currentDriverNames = isWebDataSuccess(driverNamesWebData) ? driverNamesWebData.data : {};

  const isCurrentEmpty = Object.keys(currentDriverNames).length === 0;
  if (isCurrentEmpty) {
    return neededDrivers;
  }

  return neededDrivers.filter((driverId) => !currentDriverNames[driverId]);
};

const transformMaintenanceCosts = (data: NewMaintenanceCosts): MaintenanceCosts => {
  const maintenanceCosts: MaintenanceCosts = {};

  for (const vehicleData of data.rows) {
    const vehicleId = vehicleData.vehicleId;
    const costs = vehicleData.costs;

    maintenanceCosts[vehicleId] = costs.map((cost: any) => ({
      dateInMillis: new Date(cost.date).getTime(),
      preventive: cost.preventive,
      corrective: cost.corrective,
    }));
  }

  return maintenanceCosts;
};

export function* requestLoadCostsSaga(action: LoadCostsRequest) {
  try {
    const [fuelCosts, newMaintenanceCosts]: [FuelCosts, NewMaintenanceCosts] = yield all([
      call(costsRequester.getFuelCosts, action.payload),
      call(newMaintenanceRequester.getMaintenanceCosts, action.payload),
    ]);

    const maintenanceCosts = transformMaintenanceCosts(newMaintenanceCosts);

    yield put(loadCostsSucceeded(fuelCosts, maintenanceCosts));

    const driverNamesById: WebData<DriverNames> = yield select(selectDriverNamesById);
    const missingDriversId: UUID[] = yield call(getMissingDriversId, driverNamesById, fuelCosts);

    const driverNames: DriverNames = missingDriversId.length !== 0 ? yield call(driversRequester.getDriverNames) : {};
    yield put(loadDriverNamesSucceeded(driverNames));
  } catch (err) {
    yield put(loadCostsFailed());
    Alert.error('Houve um erro, carregue a página de gastos novamente.');
  }
}

export function* requestDownloadCostsSaga(action: DownloadCostsRequest) {
  const period = action.payload;

  try {
    const reportBlob: Blob = yield call(costsRequester.getCostsReport, period);
    downloadReportFile(reportBlob, 'historico_gastos', period.start, period.end);
  } catch (err) {
    Alert.error('Houve um erro, tente novamente mais tarde.');
  } finally {
    yield put(downloadCostsEnd());
  }
}

export function* CostsSagasListener() {
  yield all([takeLatest(ActionTypes.LOAD_COSTS_REQUEST, requestLoadCostsSaga), takeLatest(ActionTypes.DOWNLOAD_COSTS_REQUEST, requestDownloadCostsSaga)]);
}
